import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  useGetEventQuery,
  useGetEventReportsQuery,
  useGetEventParticipantsQuery,
  useUpdateEventStatusMutation,
  useDeleteEventMutation,
  useRemoveEventParticipantMutation,
  type AdminEventDetail,
  type AdminEventParticipant,
  type EventStatusValue,
} from '../../redux/events/events.api';
import { AddParticipantModal } from '../../components/AddParticipantModal';

const STATUS_LABEL: Record<string, string> = {
  upcoming: 'Upcoming',
  in_progress: 'In progress',
  finished: 'Finished',
  cancelled: 'Cancelled',
};

const STATUS_OPTIONS: EventStatusValue[] = [
  'upcoming',
  'in_progress',
  'finished',
  'cancelled',
];

const REASON_LABEL: Record<string, string> = {
  inappropriate_content: 'Inappropriate content',
  spam: 'Spam',
  harassment: 'Harassment',
  violence_or_threats: 'Violence or threats',
  hate_speech: 'Hate speech',
  misinformation: 'Misinformation',
  illegal_activity: 'Illegal activity',
  adult_content: 'Adult content',
  fake_event: 'Fake event',
  copyright_violation: 'Copyright violation',
  other: 'Other',
};

const REPORT_STATUS_LABEL: Record<string, string> = {
  pending: 'Pending',
  reviewed: 'Reviewed',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
};

const extractErrorMessage = (err: unknown): string => {
  if (typeof err === 'object' && err !== null) {
    const e = err as { data?: { message?: string }; status?: number | string };
    if (e.data?.message) return e.data.message;
    if (e.status === 'FETCH_ERROR') return 'Network error.';
  }
  return 'Action failed';
};

const statusClass = (status: string): string => {
  const slug = STATUS_LABEL[status] ? status : 'default';
  return `event-detail__status event-detail__status--${slug}`;
};

const formatDateTime = (
  dateIso: string | null,
  time: string | null,
): string => {
  if (!dateIso) return '—';
  const d = new Date(dateIso);
  if (Number.isNaN(d.getTime())) return dateIso;
  const dateStr = d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return time ? `${dateStr} · ${time}` : dateStr;
};

const initial = (firstName?: string, lastName?: string): string => {
  return (firstName || lastName || '?').charAt(0).toUpperCase();
};

// Symbol-prefix the common currencies; everything else (CZK, PLN, HUF, AED…)
// reads clearest as "300 CZK". Falls back to EUR when the field is missing.
const CURRENCY_SYMBOL: Record<string, string> = {
  EUR: '€',
  USD: '$',
  GBP: '£',
  UAH: '₴',
};

const formatFee = (fee: number | null, currency: string | null): string => {
  if (fee == null) return 'Free';
  const code = (currency || 'EUR').toUpperCase();
  const symbol = CURRENCY_SYMBOL[code];
  return symbol ? `${symbol}${fee}` : `${fee} ${code}`;
};

interface FieldProps {
  label: string;
  value: React.ReactNode;
}

const Field = ({ label, value }: FieldProps): React.JSX.Element => (
  <div className="user-detail__field">
    <span className="user-detail__field-label">{label}</span>
    <span className="user-detail__field-value">{value || '—'}</span>
  </div>
);

interface MiniUserProps {
  to: string;
  firstName: string;
  lastName: string;
  avatarUrl?: string | null;
}

const MiniUser = ({
  to,
  firstName,
  lastName,
  avatarUrl,
}: MiniUserProps): React.JSX.Element => (
  <Link className="event-detail__user" to={to}>
    {avatarUrl ? (
      <img className="event-detail__user-avatar" src={avatarUrl} alt="" />
    ) : (
      <div className="event-detail__user-avatar event-detail__user-avatar--blank">
        {initial(firstName, lastName)}
      </div>
    )}
    <span className="event-detail__user-name">
      {firstName} {lastName}
    </span>
  </Link>
);

export const AdminEventDetailPage = (): React.JSX.Element => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: e, isLoading, error } = useGetEventQuery(id ?? '', {
    skip: !id,
  });
  const { data: reports } = useGetEventReportsQuery(id ?? '', {
    skip: !id,
  });
  const { data: participants } = useGetEventParticipantsQuery(id ?? '', {
    skip: !id,
  });
  const [updateStatus, { isLoading: isStatusSaving }] =
    useUpdateEventStatusMutation();
  const [deleteEvent, { isLoading: isDeleting }] = useDeleteEventMutation();
  const [removeParticipant, { isLoading: isRemovingParticipant }] =
    useRemoveEventParticipantMutation();
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);

  if (isLoading) {
    return <div className="admin-users__loading">Loading event…</div>;
  }

  if (error || !e) {
    return (
      <div className="user-detail">
        <Link to="/admin/dashboard/events" className="user-detail__back">
          ← Back to events
        </Link>
        <div className="admin-users__error">
          {error && 'status' in error && error.status === 404
            ? 'Event not found.'
            : 'Failed to load event.'}
        </div>
      </div>
    );
  }

  const event: AdminEventDetail = e;
  const start = formatDateTime(event.startDate, event.startTime);
  const end = formatDateTime(event.endDate, event.endTime);
  const canCancel =
    event.status === 'upcoming' || event.status === 'in_progress';

  const handleStatusChange = async (
    sel: React.ChangeEvent<HTMLSelectElement>,
  ): Promise<void> => {
    const next = sel.target.value as EventStatusValue;
    if (next === event.status) return;
    if (
      !window.confirm(
        `Change status from "${STATUS_LABEL[event.status] ?? event.status}" to "${STATUS_LABEL[next] ?? next}"?`,
      )
    ) {
      sel.target.value = event.status;
      return;
    }
    setActionError(null);
    try {
      await updateStatus({ id: event.id, status: next }).unwrap();
    } catch (err) {
      setActionError(extractErrorMessage(err));
      sel.target.value = event.status;
    }
  };

  const handleCancel = async (): Promise<void> => {
    if (!window.confirm(`Cancel "${event.name}"? Participants will see it as cancelled.`)) {
      return;
    }
    setActionError(null);
    try {
      await updateStatus({ id: event.id, status: 'cancelled' }).unwrap();
    } catch (err) {
      setActionError(extractErrorMessage(err));
    }
  };

  const handleRemoveParticipant = async (
    p: AdminEventParticipant,
  ): Promise<void> => {
    if (!p.user) return;
    const name = `${p.user.firstName} ${p.user.lastName}`.trim();
    if (
      !window.confirm(`Remove ${name || p.user.email} from "${event.name}"?`)
    ) {
      return;
    }
    setActionError(null);
    setRemovingId(p.userId);
    try {
      await removeParticipant({
        eventId: event.id,
        userId: p.userId,
      }).unwrap();
    } catch (err) {
      setActionError(extractErrorMessage(err));
    } finally {
      setRemovingId(null);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (
      !window.confirm(
        `Permanently delete "${event.name}"?\n\nThis removes the event, all applications, reports, co-creators, feedbacks, waitlist entries, chat messages, and notification preferences. This cannot be undone.`,
      )
    ) {
      return;
    }
    setActionError(null);
    try {
      await deleteEvent(event.id).unwrap();
      navigate('/admin/dashboard/events', { replace: true });
    } catch (err) {
      setActionError(extractErrorMessage(err));
    }
  };

  return (
    <div className="user-detail">
      <Link to="/admin/dashboard/events" className="user-detail__back">
        ← Back to events
      </Link>

      <div className="event-detail__hero">
        <div className="event-detail__hero-image">
          {event.imageUrl ? (
            <img src={event.imageUrl} alt="" />
          ) : (
            <div className="event-detail__hero-image-blank" />
          )}
        </div>
        <div className="event-detail__hero-info">
          <h2 className="user-detail__name">{event.name}</h2>
          <div className="user-detail__badges">
            <span className={statusClass(event.status)}>
              {STATUS_LABEL[event.status] || event.status}
            </span>
            {event.onlyForClubMembers && (
              <span className="user-detail__badge user-detail__badge--premium">
                Club only
              </span>
            )}
            {event.source && event.source !== 'local' && (
              <span className="user-detail__badge user-detail__badge--verified">
                {event.source}
              </span>
            )}
          </div>
          <div className="event-detail__hero-meta">
            <div>{start}</div>
            {event.location?.city && <div>📍 {event.location.city}</div>}
            <div
              className="event-detail__hero-id"
              title="Click to copy"
              onClick={() => navigator.clipboard?.writeText(event.id)}
            >
              ID: {event.id}
            </div>
          </div>
        </div>
      </div>

      <section className="user-detail__card user-detail__card--actions">
        <h3 className="user-detail__card-title">Actions</h3>
        <div className="user-detail__action-row">
          <label className="user-detail__action-label">Status</label>
          <select
            className="user-detail__role-select"
            value={event.status}
            onChange={handleStatusChange}
            disabled={isStatusSaving}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </div>
        <div className="user-detail__action-row user-detail__action-row--buttons">
          {canCancel && (
            <button
              type="button"
              className="user-detail__action-btn user-detail__action-btn--ban"
              onClick={handleCancel}
              disabled={isStatusSaving}
            >
              {isStatusSaving ? 'Saving…' : 'Cancel event'}
            </button>
          )}
          <button
            type="button"
            className="user-detail__action-btn user-detail__action-btn--delete"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting…' : 'Delete event'}
          </button>
        </div>
        {actionError && (
          <div className="admin-users__error" style={{ marginTop: 12 }}>
            {actionError}
          </div>
        )}
      </section>

      <section className="user-detail__card user-detail__card--wide">
        <div className="event-detail__section-head">
          <h3 className="user-detail__card-title" style={{ margin: 0 }}>
            Participants{' '}
            {participants && (
              <span className="event-detail__reports-count event-detail__reports-count--neutral">
                {participants.length}
              </span>
            )}
          </h3>
          <button
            type="button"
            className="event-detail__add-btn"
            onClick={() => setAddModalOpen(true)}
          >
            + Add
          </button>
        </div>
        {!participants && (
          <p className="user-detail__empty">Loading participants…</p>
        )}
        {participants && participants.length === 0 && (
          <p className="user-detail__empty">No participants yet.</p>
        )}
        {participants && participants.length > 0 && (
          <div className="event-detail__participants">
            {participants.map((p) => {
              const u = p.user;
              const name = u
                ? `${u.firstName} ${u.lastName}`.trim() || u.email
                : '—';
              return (
                <div key={p.applicationId} className="event-detail__participant">
                  {u ? (
                    <Link
                      to={`/admin/dashboard/users/${u.id}`}
                      className="event-detail__user"
                    >
                      {u.avatarUrl ? (
                        <img
                          src={u.avatarUrl}
                          alt=""
                          className="event-detail__user-avatar"
                        />
                      ) : (
                        <div className="event-detail__user-avatar event-detail__user-avatar--blank">
                          {(u.firstName || u.email).charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span className="event-detail__user-name">{name}</span>
                    </Link>
                  ) : (
                    <span className="event-detail__user-name">—</span>
                  )}
                  <div className="event-detail__participant-meta">
                    {p.isFree && (
                      <span className="event-detail__free-badge">FREE</span>
                    )}
                    <span className="event-detail__participant-date">
                      Joined{' '}
                      {new Date(p.joinedAt).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </span>
                    <button
                      type="button"
                      className="event-detail__remove-btn"
                      onClick={() => handleRemoveParticipant(p)}
                      disabled={
                        isRemovingParticipant && removingId === p.userId
                      }
                    >
                      {isRemovingParticipant && removingId === p.userId
                        ? 'Removing…'
                        : 'Remove'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <section className="user-detail__card user-detail__card--wide">
        <h3 className="user-detail__card-title">
          Reports{' '}
          {reports && (
            <span className="event-detail__reports-count">
              {reports.length}
            </span>
          )}
        </h3>
        {!reports && <p className="user-detail__empty">Loading reports…</p>}
        {reports && reports.length === 0 && (
          <p className="user-detail__empty">No reports for this event.</p>
        )}
        {reports && reports.length > 0 && (
          <div className="event-detail__reports">
            {reports.map((r) => (
              <div key={r.id} className="event-detail__report">
                <div className="event-detail__report-head">
                  <span
                    className={`event-detail__report-reason event-detail__report-reason--${r.reason}`}
                  >
                    {REASON_LABEL[r.reason] ?? r.reason}
                  </span>
                  <span
                    className={`event-detail__report-status event-detail__report-status--${r.status}`}
                  >
                    {REPORT_STATUS_LABEL[r.status] ?? r.status}
                  </span>
                  <span className="event-detail__report-date">
                    {new Date(r.createdAt).toLocaleString(undefined, {
                      dateStyle: 'medium',
                      timeStyle: 'short',
                    })}
                  </span>
                </div>
                {r.details && (
                  <p className="event-detail__report-details">{r.details}</p>
                )}
                {r.reporter && (
                  <div className="event-detail__report-reporter">
                    By:{' '}
                    <Link to={`/admin/dashboard/users/${r.reporter.id}`}>
                      {r.reporter.firstName} {r.reporter.lastName}
                    </Link>
                  </div>
                )}
                {r.moderatorNotes && (
                  <div className="event-detail__report-mod-notes">
                    <strong>Moderator notes:</strong> {r.moderatorNotes}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="user-detail__grid">
        <section className="user-detail__card user-detail__card--wide">
          <h3 className="user-detail__card-title">Description</h3>
          {event.description ? (
            <p className="event-detail__description">{event.description}</p>
          ) : (
            <p className="user-detail__empty">No description.</p>
          )}
        </section>

        <section className="user-detail__card">
          <h3 className="user-detail__card-title">When</h3>
          <Field label="Starts" value={start} />
          <Field label="Ends" value={end} />
          <Field label="Timezone" value={event.timezone} />
        </section>

        <section className="user-detail__card">
          <h3 className="user-detail__card-title">Where</h3>
          <Field label="Address" value={event.location?.address} />
          <Field label="City" value={event.location?.city} />
          <Field label="Country" value={event.location?.country} />
          {event.location?.latitude != null &&
            event.location?.longitude != null && (
              <Field
                label="Coords"
                value={
                  <span className="user-detail__coords">
                    {event.location.latitude.toFixed(5)},{' '}
                    {event.location.longitude.toFixed(5)}
                  </span>
                }
              />
            )}
        </section>

        <section className="user-detail__card">
          <h3 className="user-detail__card-title">Capacity & rules</h3>
          <Field
            label="Capacity"
            value={
              event.capacity
                ? `${event.capacity.min}–${event.capacity.max}`
                : null
            }
          />
          <Field
            label="Free slots used"
            value={
              event.freeSlotsTotal != null
                ? `${event.freeSlotsTotal - (event.freeSlotsLeft ?? event.freeSlotsTotal)} / ${event.freeSlotsTotal}`
                : null
            }
          />
          <Field label="Min age" value={event.minAge} />
          <Field label="Max age" value={event.maxAge} />
          <Field label="Fee" value={formatFee(event.fee, event.currency)} />
          <Field label="Category" value={event.category} />
        </section>

        <section className="user-detail__card">
          <h3 className="user-detail__card-title">Author</h3>
          {event.author ? (
            <MiniUser
              to={`/admin/dashboard/users/${event.author.id}`}
              firstName={event.author.firstName}
              lastName={event.author.lastName}
              avatarUrl={event.author.avatarUrl}
            />
          ) : (
            <p className="user-detail__empty">No author recorded.</p>
          )}
        </section>

        {event.club && (
          <section className="user-detail__card">
            <h3 className="user-detail__card-title">Club</h3>
            <div className="event-detail__user">
              {event.club.imageUrl ? (
                <img
                  className="event-detail__user-avatar"
                  src={event.club.imageUrl}
                  alt=""
                />
              ) : (
                <div className="event-detail__user-avatar event-detail__user-avatar--blank">
                  {initial(event.club.name)}
                </div>
              )}
              <span className="event-detail__user-name">{event.club.name}</span>
            </div>
          </section>
        )}

        <section className="user-detail__card user-detail__card--wide">
          <h3 className="user-detail__card-title">Co-creators</h3>
          {event.coCreators && event.coCreators.length > 0 ? (
            <div className="event-detail__user-grid">
              {event.coCreators.map((c) => (
                <MiniUser
                  key={c.id}
                  to={`/admin/dashboard/users/${c.id}`}
                  firstName={c.firstName}
                  lastName={c.lastName}
                  avatarUrl={c.avatarUrl}
                />
              ))}
            </div>
          ) : (
            <p className="user-detail__empty">No co-creators.</p>
          )}
        </section>

        <section className="user-detail__card user-detail__card--wide">
          <h3 className="user-detail__card-title">Tags & activities</h3>
          <Field
            label="Tags"
            value={
              event.tags && event.tags.length > 0 ? (
                <div className="user-detail__tags">
                  {event.tags.map((t) => (
                    <span key={t.id} className="user-detail__tag">
                      {t.name}
                    </span>
                  ))}
                </div>
              ) : null
            }
          />
          <Field
            label="Activities"
            value={
              event.activities && event.activities.length > 0 ? (
                <div className="user-detail__tags">
                  {event.activities.map((a) => (
                    <span key={a.id} className="user-detail__tag">
                      {a.name}
                    </span>
                  ))}
                </div>
              ) : null
            }
          />
        </section>

        {(event.source !== 'local' || event.externalId || event.ticketUrl) && (
          <section className="user-detail__card user-detail__card--wide">
            <h3 className="user-detail__card-title">Source</h3>
            <Field label="Source" value={event.source} />
            <Field label="External ID" value={event.externalId} />
            <Field
              label="Ticket URL"
              value={
                event.ticketUrl ? (
                  <a
                    className="user-detail__link"
                    href={event.ticketUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open ↗
                  </a>
                ) : null
              }
            />
          </section>
        )}
      </div>
      <AddParticipantModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        eventId={event.id}
        excludeUserIds={[
          ...(event.author ? [event.author.id] : []),
          ...event.coCreators.map((c) => c.id),
        ]}
      />
    </div>
  );
};
